/**
 * 케밥 메뉴 (⋮) — 부수 액션을 한 곳에 정리
 *
 * PrimaryCTACard의 KebabMenu 패턴을 일반 컴포넌트로 추출.
 * - useState + useRef로 토글
 * - click outside / ESC 키로 닫힘
 * - role="menu" / role="menuitem" / aria-haspopup
 * - destructive 항목은 빨강 텍스트 + 상단 구분선
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import { MoreVertical } from 'lucide-react';

export interface MoreActionsMenuItem {
  icon?: ReactNode;
  label: string;
  /** href가 있으면 Link로 렌더, 없으면 onClick 버튼 */
  href?: string;
  onClick?: () => void;
  /** 위험 액션(삭제 등) — 빨강 텍스트 + 상단 구분선 */
  destructive?: boolean;
  /** 비활성화 — 클릭 무시 + 회색 처리 */
  disabled?: boolean;
  /** 조건부 숨김 — 렌더링 자체 X */
  hidden?: boolean;
}

export interface MoreActionsMenuProps {
  items: MoreActionsMenuItem[];
  /** 메뉴 정렬 (기본 right) */
  align?: 'left' | 'right';
  /** 트리거 버튼 크기 (기본 md) */
  triggerSize?: 'sm' | 'md';
  /** aria-label (기본 "더보기") */
  triggerLabel?: string;
  /** 추가 클래스 (트리거 컨테이너에 적용) */
  className?: string;
}

const TRIGGER_SIZE_CLASSES = {
  sm: 'p-1 [&>svg]:w-4 [&>svg]:h-4',
  md: 'p-2 [&>svg]:w-5 [&>svg]:h-5',
};

export function MoreActionsMenu({
  items,
  align = 'right',
  triggerSize = 'md',
  triggerLabel = '더보기',
  className = '',
}: MoreActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 외부 클릭 닫기
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  // ESC 닫기
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className={`relative flex-shrink-0 ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={`inline-flex items-center justify-center rounded-lg hover:bg-neutral-100 text-neutral-500 transition-colors ${TRIGGER_SIZE_CLASSES[triggerSize]}`}
      >
        <MoreVertical />
      </button>
      {isOpen && (
        <div
          role="menu"
          className={`absolute z-30 mt-2 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {visibleItems.map((item, index) => {
            const isFirstDestructive =
              item.destructive && !visibleItems[index - 1]?.destructive;
            const itemClass = `w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
              item.destructive
                ? 'text-red-600 hover:bg-red-50'
                : 'text-neutral-700 hover:bg-neutral-50'
            } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
              isFirstDestructive ? 'border-t border-neutral-100' : ''
            }`;

            const inner = (
              <>
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span>{item.label}</span>
              </>
            );

            // Link 분기 (href가 있고 disabled가 아닐 때)
            if (item.href && !item.disabled) {
              return (
                <Link
                  key={`${item.label}-${index}`}
                  href={item.href}
                  role="menuitem"
                  className={itemClass}
                  onClick={() => {
                    setIsOpen(false);
                    item.onClick?.();
                  }}
                >
                  {inner}
                </Link>
              );
            }

            // button 분기 (onClick + disabled)
            return (
              <button
                key={`${item.label}-${index}`}
                type="button"
                role="menuitem"
                onClick={() => {
                  if (item.disabled) {
                    return;
                  }
                  setIsOpen(false);
                  item.onClick?.();
                }}
                disabled={item.disabled}
                className={itemClass}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MoreActionsMenu;
